<template>
  <div class="form-container">
    <a-alert 
      v-if="errorMsg" 
      type="error" 
      :message="errorMsg" 
      banner 
      closable 
      @close="$emit('update:errorMsg', '')"
      class="mb-3"
    />
    
    <a-alert 
      v-if="successMsg" 
      type="success" 
      :message="successMsg" 
      banner 
      closable 
      @close="$emit('update:successMsg', '')"
      class="mb-3"
    />

    <div class="section-title">关注公众号自动回复设置</div>
    <p class="section-description">
      设置用户关注公众号后，系统自动回复的消息内容。这是与用户的第一次互动，请确保信息清晰有用。
    </p>

    <a-form layout="vertical">
      <a-form-item label="自动回复内容">
        <div class="editor-toolbar">
          <a-button size="small" @click="openInsertModal">
            <link-outlined /> 插入小程序链接
          </a-button>
        </div>
        <a-textarea
          ref="contentTextarea"
          v-model:value="contentValue"
          :rows="40"
          placeholder="请输入关注公众号后的自动回复内容"
          :show-count="{
            formatter: ({ count }) => `${count}字`
          }"
          style="min-height: 400px;"
          @click="captureCursorPosition"
          @keyup="captureCursorPosition"
          @select="captureCursorPosition"
        />
      </a-form-item>

      <!-- 삽입된 미니앱 링크 요약 목록 (읽기 전용 + 삭제) -->
      <div v-if="detectedLinks.length" class="miniapp-links-summary">
        <div class="miniapp-links-summary__title">已包含的小程序链接：</div>
        <div
          v-for="(link, idx) in detectedLinks"
          :key="idx"
          class="miniapp-links-summary__item"
        >
          <span class="miniapp-links-summary__text">{{ link.text }}</span>
          <span class="miniapp-links-summary__meta">{{ link.appid }} · {{ link.path || '(无路径)' }}</span>
          <a-button type="link" size="small" danger @click="removeLink(link)">删除</a-button>
        </div>
      </div>

      <div class="tips-container">
        <div class="tips-title">提示信息:</div>
        <ul class="tips-list">
          <li>可以使用表情符号和简单的文本格式</li>
          <li>保存后，更改将立即生效</li>
          <li>
            <a href="https://developers.weixin.qq.com/doc/offiaccount/Message_Management/Passive_user_reply_message.html" target="_blank" rel="noopener noreferrer">
              查看微信公众号官方文档了解更多内容
            </a>
          </li>
        </ul>
      </div>

      <div class="actions-container">
        <a-button 
          type="primary" 
          size="large" 
          @click="$emit('save')" 
          :loading="saving"
          :disabled="!isContentChanged || saving"
        >
          保存设置
        </a-button>
        <a-button 
          size="large" 
          @click="$emit('reset')" 
          :disabled="!isContentChanged || saving"
        >
          取消
        </a-button>
      </div>
    </a-form>

    <!-- 小程序 링크 삽입 모달 — WeChat 공식 규칙: data-miniprogram-appid는 반드시 공중계정에 关联(연결)된 미니앱이어야 한다 -->
    <a-modal
      v-model:open="showInsertModal"
      title="插入小程序链接"
      ok-text="插入"
      cancel-text="取消"
      :confirm-loading="false"
      :ok-button-props="{ disabled: !canConfirmInsert }"
      @ok="confirmInsert"
    >
      <a-form layout="vertical">
        <a-form-item label="选择小程序" required>
          <a-select
            v-model:value="selectedAppid"
            placeholder="请选择已关联的小程序"
            :loading="miniappListLoading"
            @change="onAppidChange"
          >
            <a-select-option
              v-for="item in miniappListSelectable"
              :key="item.appid"
              :value="item.appid"
            >
              {{ item.nickname || '(未命名小程序)' }}（{{ item.appid }}）
            </a-select-option>
          </a-select>
          <div v-if="miniappListError" class="insert-modal__hint insert-modal__hint--error">
            {{ miniappListError }}
          </div>
          <div v-else-if="!miniappListLoading && miniappListSelectable.length === 0" class="insert-modal__hint">
            该公众号暂无可用的关联小程序
          </div>
        </a-form-item>

        <a-form-item label="页面路径" required>
          <a-select
            v-if="pagepathMode === 'select'"
            v-model:value="selectedPagepath"
            placeholder="请选择页面"
            @change="onPagepathSelectChange"
          >
            <a-select-option v-for="pg in miniappPages" :key="pg.path" :value="pg.path">
              {{ pg.title ? `${pg.title}（${pg.path}）` : pg.path }}
            </a-select-option>
            <a-select-option value="__direct__">直接输入…</a-select-option>
          </a-select>
          <a-input
            v-else
            v-model:value="directPagepath"
            placeholder="请输入小程序页面路径（例如 pages/index/index）"
          />
          <div v-if="miniappPagesLoading" class="insert-modal__hint">页面列表加载中…</div>
          <div v-else-if="pagepathMode === 'direct' && hasMiniappPages" class="insert-modal__hint">
            <a href="javascript:void(0)" @click="pagepathMode = 'select'">返回选择列表</a>
          </div>
        </a-form-item>

        <a-form-item label="链接文本" required>
          <a-input v-model:value="linkText" placeholder="例如：获取认证卡" />
        </a-form-item>

        <a-form-item label="href 兜底链接（可选）">
          <a-input v-model:value="hrefFallback" placeholder="微信客户端 6.5.16 以下版本的网页兜底链接" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { LinkOutlined } from '@ant-design/icons-vue';
import { detectMiniprogramLinks, buildMiniprogramLinkTag } from '../utils/miniprogramLink';

export default {
  name: 'FollowWelcomeMessage',
  components: {
    LinkOutlined
  },
  props: {
    // 오류 메시지
    errorMsg: {
      type: String,
      default: ''
    },
    // 성공 메시지
    successMsg: {
      type: String,
      default: ''
    },
    // 현재 내용
    content: {
      type: String,
      default: ''
    },
    // 원본 내용 (변경 여부 확인용)
    originalContent: {
      type: String,
      default: ''
    },
    // 저장 중 상태
    saving: {
      type: Boolean,
      default: false
    },
    // 로딩 상태
    loading: {
      type: Boolean,
      default: false
    },
    // 현재 선택된 공중계정 — 小程序 링크 삽입 도구가 gzhName-스코프로 API를 호출하는 데 사용
    gzhName: {
      type: String,
      default: ''
    },
    // { getList(gzhName), getPages(gzhName, appid) } — api.js의 miniprogram 섹션(CustomizeMenu.vue와 동일 엔드포인트 재사용)
    miniprogramApi: {
      type: Object,
      default: null
    }
  },
  emits: ['update:content', 'update:errorMsg', 'update:successMsg', 'save', 'reset'],
  data() {
    return {
      contentValue: this.content,
      // 小程序 링크 삽입 모달 상태
      showInsertModal: false,
      cursorPos: null,
      miniappList: [],
      miniappListLoading: false,
      miniappListError: '',
      miniappListFetchedFor: '',
      selectedAppid: '',
      miniappPages: [],
      miniappPagesLoading: false,
      pagepathMode: 'select', // 'select' | 'direct'
      selectedPagepath: '',
      directPagepath: '',
      linkText: '',
      hrefFallback: ''
    };
  },
  watch: {
    content(newValue) {
      this.contentValue = newValue;
    },
    contentValue(newValue) {
      this.$emit('update:content', newValue);
    }
  },
  computed: {
    isContentChanged() {
      return this.contentValue !== this.originalContent;
    },
    // 현재 콘텐츠에 포함된 미니앱 링크 목록 (읽기 전용 요약 + 삭제용)
    detectedLinks() {
      return detectMiniprogramLinks(this.contentValue);
    },
    // 关联(연결)된 미니앱 중 선택 가능한 것만 노출 — CustomizeMenu.vue appidCandidates와 동일 규칙
    miniappListSelectable() {
      return (this.miniappList || []).filter((m) => m.appid && m.selectable !== false);
    },
    hasMiniappPages() {
      return Array.isArray(this.miniappPages) && this.miniappPages.length > 0;
    },
    canConfirmInsert() {
      const path = this.pagepathMode === 'select' ? this.selectedPagepath : this.directPagepath.trim();
      return !!(this.selectedAppid && path && this.linkText.trim());
    }
  },
  methods: {
    // 텍스트영역 클릭/키입력/선택 시점의 커서 위치를 기록 — 삽입 시 커서 위치에 반영하기 위함
    captureCursorPosition() {
      const el = this.getNativeTextareaEl();
      if (el && typeof el.selectionStart === 'number') {
        this.cursorPos = el.selectionStart;
      }
    },

    // ant-design-vue Textarea의 내부 DOM 접근 — 버전에 따라 구조가 다를 수 있어 다단계 폴백 처리
    getNativeTextareaEl() {
      const comp = this.$refs.contentTextarea;
      if (!comp) return null;
      return (
        comp.resizableTextArea?.textArea ||
        comp.textAreaRef?.$el ||
        comp.textareaRef ||
        (comp.$el && comp.$el.querySelector && comp.$el.querySelector('textarea')) ||
        null
      );
    },

    // 모달 오픈 — 상태 초기화 + 관련 미니앱 목록 로드
    openInsertModal() {
      this.selectedAppid = '';
      this.miniappPages = [];
      this.pagepathMode = 'select';
      this.selectedPagepath = '';
      this.directPagepath = '';
      this.linkText = '';
      this.hrefFallback = '';
      this.captureCursorPosition();
      this.showInsertModal = true;
      this.fetchMiniappList();
    },

    async fetchMiniappList(force = false) {
      if (!this.gzhName || !this.miniprogramApi) {
        this.miniappListError = '未选择公众号，无法加载小程序列表';
        return;
      }
      if (!force && this.miniappListFetchedFor === this.gzhName && this.miniappList.length > 0) {
        this.miniappListError = '';
        this.applyDefaultMiniappSelection();
        return;
      }
      this.miniappList = [];
      this.miniappListError = '';
      this.miniappListLoading = true;
      try {
        const response = await this.miniprogramApi.getList(this.gzhName);
        this.miniappList = response.data?.miniprograms || [];
        this.miniappListFetchedFor = this.gzhName;
        this.applyDefaultMiniappSelection();
      } catch (error) {
        console.error('Error fetching miniprogram list:', error);
        this.miniappListError = error.response?.data?.message || '小程序列表加载失败';
      } finally {
        this.miniappListLoading = false;
      }
    },

    // 모달 오픈 시 첫 번째 关联 미니앱을 디폴트 선택하고 페이지 카탈로그까지 미리 로드한다
    applyDefaultMiniappSelection() {
      if (this.selectedAppid) {
        return;
      }
      const first = this.miniappListSelectable[0];
      if (first) {
        this.selectedAppid = first.appid;
        this.onAppidChange(first.appid);
      }
    },

    // 선택된 小程序의 페이지 카탈로그 조회. 카탈로그가 없으면 직접입력으로 폴백(CustomizeMenu.vue와 동일 UX 규칙)
    async onAppidChange(appid) {
      this.selectedPagepath = '';
      this.directPagepath = '';
      this.miniappPages = [];
      if (!appid || !this.gzhName || !this.miniprogramApi) {
        this.pagepathMode = 'direct';
        return;
      }
      this.miniappPagesLoading = true;
      try {
        const response = await this.miniprogramApi.getPages(this.gzhName, appid);
        const pages = response.data?.pages || [];
        this.miniappPages = pages;
        this.pagepathMode = pages.length > 0 ? 'select' : 'direct';
      } catch (error) {
        console.error('Error fetching miniapp pages:', error);
        this.miniappPages = [];
        this.pagepathMode = 'direct';
      } finally {
        this.miniappPagesLoading = false;
      }
    },

    onPagepathSelectChange(value) {
      if (value === '__direct__') {
        this.pagepathMode = 'direct';
        this.selectedPagepath = '';
      }
    },

    // 확인 시 텍스트영역 커서 위치(또는 끝)에 <a data-miniprogram-*> 태그를 삽입
    confirmInsert() {
      if (!this.canConfirmInsert) return;

      const path = this.pagepathMode === 'select' ? this.selectedPagepath : this.directPagepath.trim();
      const tag = buildMiniprogramLinkTag({
        appid: this.selectedAppid,
        path,
        text: this.linkText.trim(),
        href: this.hrefFallback
      });

      const current = this.contentValue || '';
      const pos = typeof this.cursorPos === 'number' && this.cursorPos >= 0 && this.cursorPos <= current.length
        ? this.cursorPos
        : current.length;

      this.contentValue = current.slice(0, pos) + tag + current.slice(pos);
      this.cursorPos = pos + tag.length;
      this.showInsertModal = false;
    },

    // 목록에서 링크 삭제 — 파싱 시 저장해둔 원문(raw) 매칭 부분만 정확히 제거
    removeLink(link) {
      if (!link || !link.raw) return;
      this.contentValue = (this.contentValue || '').replace(link.raw, '');
    }
  }
};
</script>

<style lang="scss" scoped>
.form-container {
  max-width: 100%;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.section-description {
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
}

.tips-container {
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border-left: 4px solid #1890ff;
}

.tips-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.tips-list {
  padding-left: 16px;
  margin-bottom: 0;
  
  li {
    margin-bottom: 4px;
    color: #666;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.actions-container {
  display: flex;
  gap: 16px;
  margin-top: 16px;
}

.mb-3 {
  margin-bottom: 1rem;
}

.editor-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.miniapp-links-summary {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 12px;
  margin-bottom: 24px;

  &__title {
    font-weight: 600;
    font-size: 13px;
    color: #333;
    margin-bottom: 6px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 13px;
  }

  &__text {
    color: #576b95;
    font-weight: 500;
  }

  &__meta {
    color: #999;
    flex: 1;
    word-break: break-all;
  }
}

.insert-modal__hint {
  margin-top: 4px;
  font-size: 12px;
  color: #999;

  &--error {
    color: #ff4d4f;
  }
}
</style>
